import React, { useState, useRef, useEffect } from 'react';
import FileUpload from './FileUpload';
import { AiOutlineCalendar } from 'react-icons/ai';
import { AiOutlineMail } from 'react-icons/ai';

function ChatInput({ onSendMessage, disabled, isSending }) {
  const [message, setMessage] = useState('');
  const [processedFiles, setProcessedFiles] = useState([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isCalendarQuery, setIsCalendarQuery] = useState(false);
  const [isGmailQuery, setIsGmailQuery] = useState(false);
  const textareaRef = useRef(null);
  
  // Auto-resize textarea as content grows
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 120); // Max height of 120px
      textarea.style.height = `${newHeight}px`;
    }
    
    // Check if the message is a calendar-related query
    const calendarKeywords = ['calendar', 'schedule', 'meeting', 'level 10', 'appointment', 'event'];
    const lowerMessage = message.toLowerCase();
    const isCalendar = calendarKeywords.some(keyword => lowerMessage.includes(keyword));
    setIsCalendarQuery(isCalendar);

    // Check if the message is a Gmail-related query
    const gmailKeywords = ['email', 'gmail', 'mail', 'inbox', 'message', 'send', 'received'];
    const isGmail = gmailKeywords.some(keyword => lowerMessage.includes(keyword));
    setIsGmailQuery(isGmail);
  }, [message]);
  
  // Focus after sending completes, but only if we weren't disabled
  useEffect(() => {
    if (!isSending && !disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isSending, disabled]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled && !isExtracting) {
      // Create a message with the extracted text from files
      let enhancedMessage = message;
      
      // If we have processed files, include their text in the message
      if (processedFiles.length > 0) {
        enhancedMessage += "\n\n--- Extracted Content from Files ---\n\n";
        processedFiles.forEach((file, index) => {
          enhancedMessage += `[File ${index + 1}: ${file.name}]\n${file.text}\n\n`;
        });
      }
      
      // Send the enhanced message along with file metadata
      onSendMessage(enhancedMessage, processedFiles.map(file => file.originalFile));
      
      // Reset state
      setMessage('');
      setProcessedFiles([]);
      setShowFileUpload(false);
      setIsCalendarQuery(false);
      setIsGmailQuery(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  
  const handleFileUpload = async (files) => {
    setIsExtracting(false);
    setProcessedFiles(files);
    setShowFileUpload(false);
    
    // Focus back on textarea after file upload
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };
  
  const toggleFileUpload = () => {
    setShowFileUpload(!showFileUpload);
  };
  
  // Comment out or remove the unused handleFileSelect function
  // const handleFileSelect = (e) => {
  //   // Unused function logic
  // };
  
  return (
    <div className="relative">
      {/* File upload area */}
      {showFileUpload && (
        <div className="mb-2 rounded-modern shadow-modern overflow-hidden">
          <FileUpload 
            onFileUpload={handleFileUpload} 
            disabled={disabled}
            onExtractingStateChange={setIsExtracting}
          />
        </div>
      )}
      
      {/* Message input area */}
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center bg-white rounded-modern border border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all duration-300 shadow-modern hover:shadow-card">
          {/* File attachment button */}
          <button
            type="button"
            onClick={toggleFileUpload}
            className={`
              px-2 md:px-3 py-2 flex items-center justify-center text-gray-500 hover:text-blue-600 transition-all duration-300
              ${showFileUpload ? 'text-blue-600' : ''}
              ${disabled || isExtracting ? 'opacity-50 cursor-not-allowed' : ''}
              min-w-[40px] min-h-[40px]
            `}
            disabled={disabled || isExtracting}
            title={showFileUpload ? "Hide file upload" : "Upload files"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
            </svg>
          </button>
          
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            disabled={disabled || isExtracting}
            className="flex-1 py-2 md:py-3 px-2 bg-transparent outline-none resize-none max-h-32 text-gray-700 text-sm md:text-base"
            rows={1}
          />
          
          <button
            type="submit"
            disabled={!message.trim() || disabled || isExtracting}
            className={`
              md:px-4 px-3 py-2 rounded-modern mx-1 md:mx-2 flex items-center justify-center transition-all duration-300 btn-pop
              min-w-[44px] min-h-[44px]
              ${!message.trim() || disabled || isExtracting 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-white bg-orange-500 hover:bg-orange-600 shadow-eos-orange hover:shadow-lg'}
            `}
          >
            {isSending ? (
              <div className="flex items-center">
                <svg className="animate-spin h-4 w-4 md:mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="hidden md:inline">Sending</span>
              </div>
            ) : (
              <>
                <span className="hidden md:inline mr-1">Send</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-5 w-5 transition-transform duration-300 ${message.trim() && !disabled && !isExtracting ? 'group-hover:translate-x-1' : ''}`} 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </>
            )}
          </button>
        </div>
        
        {/* Files badge */}
        {processedFiles.length > 0 && (
          <div className="absolute -top-7 md:-top-8 left-0 bg-blue-100 text-blue-800 text-xs font-medium px-2 md:px-3 py-1 rounded-full flex items-center shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
            </svg>
            {processedFiles.length} {processedFiles.length === 1 ? 'file' : 'files'}
            <button
              type="button"
              onClick={() => setProcessedFiles([])}
              className="ml-1 text-blue-600 hover:text-blue-800 p-1"
              title="Remove files"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Display Gmail or Calendar notification based on message content */}
        {message.trim() && !isExtracting && (
          <>
            {/* Gmail notification */}
            {isGmailQuery && !isCalendarQuery && (
              <div className="absolute -top-7 md:-top-8 right-0 bg-indigo-50 text-indigo-700 text-xs font-medium px-2 md:px-3 py-1 rounded-full flex items-center shadow-sm border border-indigo-100">
                <AiOutlineMail className="h-3 w-3 mr-1 text-indigo-500" />
                <span className="text-[10px] md:text-xs">Will connect with Gmail</span>
              </div>
            )}
            
            {/* Calendar notification */}
            {isCalendarQuery && (
              <div className="absolute -top-7 md:-top-8 right-0 bg-blue-50 text-blue-700 text-xs font-medium px-2 md:px-3 py-1 rounded-full flex items-center shadow-sm border border-blue-100">
                <AiOutlineCalendar className="h-3 w-3 mr-1 text-blue-500" />
                <span className="text-[10px] md:text-xs">Will connect with Calendar</span>
              </div>
            )}
            
            {/* Combined notification for both Gmail and Calendar */}
            {isGmailQuery && isCalendarQuery && (
              <div className="absolute -top-14 md:-top-16 right-0 bg-purple-50 text-purple-700 text-xs font-medium px-2 md:px-3 py-1 rounded-full flex items-center shadow-sm border border-purple-100">
                <AiOutlineMail className="h-3 w-3 mr-1 text-purple-500" />
                <AiOutlineCalendar className="h-3 w-3 mr-1 text-purple-500" />
                <span className="text-[10px] md:text-xs">Will connect with Gmail & Calendar</span>
              </div>
            )}
          </>
        )}
        
        {/* Extracting indicator */}
        {isExtracting && (
          <div className="absolute -top-7 md:-top-8 right-0 bg-orange-100 text-orange-800 text-xs font-medium px-2 md:px-3 py-1 rounded-full flex items-center animate-pulse shadow-sm">
            <svg className="animate-spin mr-1 h-3 w-3 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-[10px] md:text-xs">Extracting...</span>
          </div>
        )}
      </form>
    </div>
  );
}

export default ChatInput;
